import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import * as attributeGroupsApi from '../api/catalogAttributeGroupsApi';
import * as attributesApi from '../api/catalogAttributesApi';
import * as categoriesApi from '../api/catalogCategoriesApi';
import * as customerGroupsApi from '../api/catalogCustomerGroupsApi';
import * as feedImportApi from '../api/catalogFeedImportApi';
import * as importApi from '../api/catalogImportApi';
import * as languagesApi from '../api/catalogLanguagesApi';
import * as markdownPlansApi from '../api/catalogMarkdownPlansApi';
import * as measurementFamiliesApi from '../api/catalogMeasurementFamiliesApi';
import * as mediaApi from '../api/catalogMediaApi';
import * as batchesApi from '../api/catalogPriceBatchesApi';
import * as priceListsApi from '../api/catalogPriceListsApi';
import type { ListPriceListLinesParams } from '../api/catalogPriceListsApi';
import * as pricingApi from '../api/catalogPricingApi';
import type { ListPricingRulesParams, ResolvePriceParams } from '../api/catalogPricingApi';
import * as productFamiliesApi from '../api/catalogProductFamiliesApi';
import * as productsApi from '../api/catalogProductsApi';
import * as publicationApi from '../api/catalogPublicationApi';
import * as unitsApi from '../api/catalogUnitsApi';
import * as productStockApi from '../api/productStockApi';
import type {
  BulkQuickSetStockRequest,
  BulkQuickSetStockResponse,
  ProductStockItemListResponse,
  ProductStockLocation,
  QuickSetStockRequest,
  QuickSetStockResponse,
} from '../model/productStockTypes';
import type {
  PriceListBindingResponse,
  PriceListRequest,
  PriceListResponse,
  RetailStoreOption,
} from '../model/priceListTypes';
import type {
  CreatePriceChangeBatchRequest,
  ListPriceChangeBatchesParams,
  PriceChangeBatchDetailResponse,
  PriceChangeBatchResponse,
} from '../model/priceBatchTypes';
import type {
  CreateMarkdownPlanRequest,
  ListMarkdownPlansParams,
  MarkdownPlanDetailResponse,
  MarkdownPlanResponse,
} from '../model/markdownPlanTypes';
import type {
  CustomerGroupMemberRequest,
  CustomerGroupMemberResponse,
  CustomerGroupRequest,
  CustomerGroupResponse,
} from '../model/customerGroupTypes';
import type {
  BindPublicationOverlayCatalogRequest,
  CatalogPreviewResult,
  CreatePublicationCatalogRequest,
  CreatePublicationCatalogRuleRequest,
  EffectivePublicationProfile,
  PublicationCatalog,
  PublicationCatalogChannelBinding,
  PublicationCatalogRule,
  PublicationCatalogType,
  PublicationProfileOverride,
  PublicationProfileOverrideRequest,
  PublicationRun,
  PublicationRunDetail,
  PublicationRunsPage,
  PublicationSalesChannel,
  SetPublicationPrimaryCatalogRequest,
  UpdatePublicationCatalogRequest,
  UpdatePublicationCatalogRuleRequest,
} from '../model/publicationTypes';
import type {
  AssetResponse,
  AttributeCreateRequest,
  AttributeDefinition,
  AttributeGroup,
  AttributeGroupCreateRequest,
  AttributeGroupUpdateRequest,
  AttributeOption,
  AttributeOptionInput,
  AttributeOptionUpdateRequest,
  AttributeUpdateRequest,
  AttributeValueSetRequest,
  BrowseCategoriesParams,
  BrowseProductsParams,
  CatalogFeedColumnMapping,
  CatalogFeedColumnMappingRequest,
  CatalogFeedFile,
  CatalogLanguage,
  CatalogLanguageCreateRequest,
  CatalogLanguageUpdateRequest,
  CatalogVersionDetail,
  CatalogVersionSummaryPage,
  Category,
  CategoryDetail,
  CategoryListRequest,
  CategoryListResponse,
  CategorySummaryPage,
  CategoryTranslation,
  CategoryTreeNode,
  CategoryUpsertRequest,
  BasketPricingRequest,
  BasketPricingResponse,
  CheckoutPricingRequest,
  CheckoutPricingResponse,
  CreatePricingSnapshotRequest,
  DiscountRoundingPolicyRequest,
  DiscountRoundingPolicyResponse,
  MediaOwnerType,
  MediaVisibility,
  MeasurementFamily,
  MeasurementFamilyCreateRequest,
  MeasurementFamilyUpdateRequest,
  ExternalProviderConfigRequest,
  ExternalProviderConfigResponse,
  GoogleSheetSettings,
  GoogleSheetSettingsRequest,
  GovernedPricePage,
  GovernedPriceResponse,
  ImportResult,
  ImportRun,
  OkeiUnit,
  PriceLifecycleAuditPage,
  PriceLineRequest,
  PriceLineResponse,
  PriceProtectionPolicyRequest,
  PriceProtectionPolicyResponse,
  PricingAuditLogPage,
  PricingQuoteResponse,
  PricingRulePage,
  PricingRuleRequest,
  PricingRuleResponse,
  PricingRuleUpdateRequest,
  PricingSnapshotResponse,
  ProductBarcode,
  ProductBarcodeCreateRequest,
  ProductDetail,
  ProductFamily,
  ProductFamilyAssignRequest,
  ProductFamilyAttribute,
  ProductFamilyAttributeInput,
  ProductFamilyAttributeUpdateRequest,
  ProductFamilyCreateRequest,
  ProductFamilyUpdateRequest,
  ProductListRequest,
  ProductListResponse,
  ProductPriceResponse,
  ProductPublication,
  PromoValidationRequest,
  PromoValidationResponse,
  ProviderCredentialRequest,
  ProviderCredentialStatusResponse,
  ProductSearchRequest,
  ProductSearchResponse,
  ProductStateUpdateRequest,
  ProductSummaryPage,
  ProductTranslation,
  ProductUpsertRequest,
  ProductVariantCreateRequest,
  ResolvedPriceResponse,
  RoundingPolicyRequest,
  RoundingPolicyResponse,
  SalesChannelResponse,
  SalesChannelUpdateRequest,
  TenantPricingConfigRequest,
  TenantPricingConfigResponse,
  UploadAssetParams,
} from '../model/types';
import { catalogKeys, OKEI_UNITS_KEY } from './queryKeys';

type Opts = { enabled?: boolean };
type Err = NormalizedApiError;

// ── OKEI unit-of-measure dictionary (plan-catalog-008) ───────────────────────
/**
 * Reference data — global (not tenant-scoped) and effectively static, so it's cached aggressively
 * (staleTime 1h) to avoid a refetch on every product-card open. Feeds the unit Autocomplete on
 * `ProductEditPage` that replaces the legacy free-text `unit` field.
 */
export function useOkeiUnits(options?: Opts): UseQueryResult<OkeiUnit[], Err> {
  return useQuery<OkeiUnit[], Err>({
    queryKey: OKEI_UNITS_KEY,
    queryFn: unitsApi.fetchOkeiUnits,
    enabled: options?.enabled ?? true,
    staleTime: 60 * 60_000,
  });
}

// ── Products ────────────────────────────────────────────────────────────────
export function useProducts(
  req: ProductListRequest,
  options?: Opts,
): UseQueryResult<ProductListResponse, Err> {
  return useQuery<ProductListResponse, Err>({
    queryKey: catalogKeys.productsList(req),
    queryFn: () => productsApi.listProducts(req),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useSearchProducts(
  req: ProductSearchRequest,
  options?: Opts,
): UseQueryResult<ProductSearchResponse, Err> {
  return useQuery<ProductSearchResponse, Err>({
    queryKey: catalogKeys.productsSearch(req),
    queryFn: () => productsApi.searchProducts(req),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

/** Browse the FULL master catalog (drafts included) — epic-catalog-002, story-024. */
export function useBrowseProducts(
  params: BrowseProductsParams,
  options?: Opts,
): UseQueryResult<ProductSummaryPage, Err> {
  return useQuery<ProductSummaryPage, Err>({
    queryKey: catalogKeys.productsBrowse(params),
    queryFn: () => productsApi.browseProducts(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (productId) => productsApi.deleteProduct(productId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.products }),
  });
}

export function useProduct(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<ProductDetail, Err> {
  return useQuery<ProductDetail, Err>({
    queryKey: catalogKeys.product(productId ?? ''),
    queryFn: () => productsApi.getProduct(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

export function useUpsertProduct() {
  const qc = useQueryClient();
  return useMutation<ImportResult, Err, { productId: string; body: ProductUpsertRequest }>({
    mutationFn: ({ productId, body }) => productsApi.upsertProduct(productId, body),
    onSuccess: (_data, { productId }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.products });
      void qc.invalidateQueries({ queryKey: catalogKeys.product(productId) });
      void qc.invalidateQueries({ queryKey: catalogKeys.productTranslations(productId) });
    },
  });
}

/**
 * Every stored translation of a product (adr-tenant-005, plan-tenant-004 Wave 2) — feeds the
 * card-language switcher's per-locale drafts (`ProductEditPage`).
 */
export function useProductTranslations(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<ProductTranslation[], Err> {
  return useQuery<ProductTranslation[], Err>({
    queryKey: catalogKeys.productTranslations(productId ?? ''),
    queryFn: () => productsApi.getProductTranslations(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

/**
 * Quick set-to-value stock edit (plan-catalog-007): a MANUAL_CORRECTION inventory adjustment via
 * the BFF — refreshes the grid (availability enrichment) and the product-360 stock panel.
 */
export function useQuickSetStock() {
  const qc = useQueryClient();
  return useMutation<QuickSetStockResponse, Err, QuickSetStockRequest>({
    mutationFn: (body) => productStockApi.quickSetStock(body),
    onSuccess: (_data, body) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.products });
      void qc.invalidateQueries({ queryKey: catalogKeys.productStock(body.productId) });
    },
  });
}

export function useUpdateProductState() {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Err, { productId: string; body: ProductStateUpdateRequest }>({
    mutationFn: ({ productId, body }) => productsApi.updateProductState(productId, body),
    onSuccess: (_data, { productId }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.products });
      void qc.invalidateQueries({ queryKey: catalogKeys.product(productId) });
      void qc.invalidateQueries({ queryKey: catalogKeys.productPublication(productId) });
    },
  });
}

export function useBulkQuickSetStock() {
  const qc = useQueryClient();
  return useMutation<BulkQuickSetStockResponse, Err, BulkQuickSetStockRequest>({
    mutationFn: (body) => productStockApi.bulkQuickSetStock(body),
    onSuccess: (_data, body) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.products });
      for (const item of body.items) {
        void qc.invalidateQueries({ queryKey: catalogKeys.productStock(item.productId) });
      }
    },
  });
}

// ── Products: publication diagnostics and master lifecycle ────────────────────
export function useProductPublication(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<ProductPublication, Err> {
  return useQuery<ProductPublication, Err>({
    queryKey: catalogKeys.productPublication(productId ?? ''),
    queryFn: () => productsApi.getProductPublication(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

function onPublicationLifecycleChanged(qc: ReturnType<typeof useQueryClient>, data: ProductPublication) {
  void qc.invalidateQueries({ queryKey: catalogKeys.products });
  void qc.invalidateQueries({ queryKey: catalogKeys.product(data.productId) });
  void qc.invalidateQueries({ queryKey: catalogKeys.productPublication(data.productId) });
}

export function useArchiveProduct() {
  const qc = useQueryClient();
  return useMutation<ProductPublication, Err, { productId: string }>({
    mutationFn: ({ productId }) => productsApi.archiveProduct(productId),
    onSuccess: (data) => onPublicationLifecycleChanged(qc, data),
  });
}

export function useRestoreProduct() {
  const qc = useQueryClient();
  return useMutation<ProductPublication, Err, { productId: string }>({
    mutationFn: ({ productId }) => productsApi.restoreProduct(productId),
    onSuccess: (data) => onPublicationLifecycleChanged(qc, data),
  });
}

// ── Products: PIM v2 — family assignment, typed attribute values (epic-catalog-004/005,
//    user-story-merchant-portal-030) ─────────────────────────────────────────────────────────

/** Shared cache update after a family/attribute-value mutation — the response IS the fresh product card. */
function onProductDetailChanged(qc: ReturnType<typeof useQueryClient>, data: ProductDetail) {
  qc.setQueryData(catalogKeys.product(data.id), data);
  // The browse/list projections surface family/attribute-driven filtering (story-033), so a
  // structural change to one product invalidates them too, not just its own detail cache entry.
  void qc.invalidateQueries({ queryKey: catalogKeys.products });
}

export function useAssignProductFamily() {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Err, { productId: string; body: ProductFamilyAssignRequest }>({
    mutationFn: ({ productId, body }) => productsApi.assignProductFamily(productId, body),
    onSuccess: (data) => onProductDetailChanged(qc, data),
  });
}

export function useUnassignProductFamily() {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Err, { productId: string }>({
    mutationFn: ({ productId }) => productsApi.unassignProductFamily(productId),
    onSuccess: (data) => onProductDetailChanged(qc, data),
  });
}

export function useSetProductAttributeValue() {
  const qc = useQueryClient();
  return useMutation<
    ProductDetail,
    Err,
    { productId: string; attributeCode: string; body: AttributeValueSetRequest }
  >({
    mutationFn: ({ productId, attributeCode, body }) =>
      productsApi.setProductAttributeValue(productId, attributeCode, body),
    onSuccess: (data) => onProductDetailChanged(qc, data),
  });
}

export function useDeleteProductAttributeValue() {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Err, { productId: string; attributeCode: string }>({
    mutationFn: ({ productId, attributeCode }) =>
      productsApi.deleteProductAttributeValue(productId, attributeCode),
    onSuccess: (data) => onProductDetailChanged(qc, data),
  });
}

// ── Products: variants (epic-catalog-004, user-story-merchant-portal-031) ──────────────────

export function useProductVariants(
  productId: string | undefined,
  locale?: string,
  options?: Opts,
): UseQueryResult<ProductDetail[], Err> {
  return useQuery<ProductDetail[], Err>({
    queryKey: catalogKeys.productVariants(productId ?? '', locale),
    queryFn: () => productsApi.listProductVariants(productId as string, locale),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

function invalidateVariantQueries(qc: ReturnType<typeof useQueryClient>, productId: string) {
  void qc.invalidateQueries({ queryKey: catalogKeys.productVariants(productId) });
  void qc.invalidateQueries({ queryKey: catalogKeys.product(productId) });
  void qc.invalidateQueries({ queryKey: catalogKeys.products });
}

/** Single-combination create — used when generating exactly one variant. */
export function useCreateProductVariant() {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Err, { productId: string; body: ProductVariantCreateRequest }>({
    mutationFn: ({ productId, body }) => productsApi.createProductVariant(productId, body),
    onSuccess: (_data, { productId }) => invalidateVariantQueries(qc, productId),
  });
}

/**
 * Server-side one-action matrix generation (epic-catalog-007, P8) — supersedes the client-side
 * fan-out for whole-matrix creation: combination math and SKU templates run in
 * web-catalog-master-service, idempotently skipping existing combinations.
 */
export function useGenerateVariantMatrix() {
  const qc = useQueryClient();
  return useMutation<
    import('../model/types').VariantMatrixGenerateResult,
    Err,
    { productId: string; body: import('../model/types').VariantMatrixGenerateRequest }
  >({
    mutationFn: ({ productId, body }) => productsApi.generateVariantMatrix(productId, body),
    onSuccess: (_data, { productId }) => invalidateVariantQueries(qc, productId),
  });
}

/** Resets a variant's own photos back to live parent inheritance (epic-catalog-007). */
export function useResetProductImagesOverride() {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Err, { productId: string }>({
    mutationFn: ({ productId }) => productsApi.resetProductImagesOverride(productId),
    onSuccess: (_d, { productId }) => invalidateVariantQueries(qc, productId),
  });
}

/** Resets a variant's own category back to live parent inheritance (epic-catalog-007). */
export function useResetProductCategoryOverride() {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Err, { productId: string }>({
    mutationFn: ({ productId }) => productsApi.resetProductCategoryOverride(productId),
    onSuccess: (_d, { productId }) => invalidateVariantQueries(qc, productId),
  });
}

export interface VariantCreationResult {
  succeeded: ProductDetail[];
  failed: { request: ProductVariantCreateRequest; message: string; code?: string }[];
}

/**
 * Fan out N variant-creation requests over the single-combination endpoint (no batch endpoint
 * exists — see `createProductVariant`'s doc). `Promise.allSettled` keeps a duplicate-combination
 * `409` on one row from aborting the rest.
 */
export function useCreateProductVariants() {
  const qc = useQueryClient();
  return useMutation<
    VariantCreationResult,
    Err,
    { productId: string; requests: ProductVariantCreateRequest[] }
  >({
    mutationFn: async ({ productId, requests }) => {
      const settled = await Promise.allSettled(
        requests.map((body) => productsApi.createProductVariant(productId, body)),
      );
      const succeeded: ProductDetail[] = [];
      const failed: VariantCreationResult['failed'] = [];
      settled.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          succeeded.push(result.value);
        } else {
          const reason = result.reason as Partial<Err> | undefined;
          failed.push({
            request: requests[index],
            message: reason?.message ?? 'An unexpected error occurred.',
            code: reason?.code,
          });
        }
      });
      return { succeeded, failed };
    },
    onSuccess: (_data, { productId }) => invalidateVariantQueries(qc, productId),
  });
}

// ── Products: version history (epic-catalog-005, user-story-merchant-portal-032) ───────────

export function useProductVersions(
  productId: string | undefined,
  params: { page: number; size: number },
  options?: Opts,
): UseQueryResult<CatalogVersionSummaryPage, Err> {
  return useQuery<CatalogVersionSummaryPage, Err>({
    queryKey: catalogKeys.productVersions(productId ?? '', params),
    queryFn: () => productsApi.getProductVersions(productId as string, params),
    enabled: (options?.enabled ?? true) && Boolean(productId),
    placeholderData: keepPreviousData,
  });
}

export function useProductVersion(
  productId: string | undefined,
  number: number | undefined,
  options?: Opts,
): UseQueryResult<CatalogVersionDetail, Err> {
  return useQuery<CatalogVersionDetail, Err>({
    queryKey: catalogKeys.productVersion(productId ?? '', number ?? 0),
    queryFn: () => productsApi.getProductVersion(productId as string, number as number),
    enabled: (options?.enabled ?? true) && Boolean(productId) && number !== undefined,
  });
}

export function useRestoreProductVersion() {
  const qc = useQueryClient();
  return useMutation<ProductDetail, Err, { productId: string; number: number }>({
    mutationFn: ({ productId, number }) => productsApi.restoreProductVersion(productId, number),
    onSuccess: (data, { productId }) => {
      onProductDetailChanged(qc, data);
      // `{}` prefix-matches every page/size of this product's version list (same wildcard idiom
      // as `catalogKeys.approvals({})` in useSubmitPriceLine, above).
      void qc.invalidateQueries({ queryKey: catalogKeys.productVersions(productId, {}) });
    },
  });
}

// ── Products: barcodes (epic-002, user-story-merchant-portal-039) ───────────────────────────

export function useProductBarcodes(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<ProductBarcode[], Err> {
  return useQuery<ProductBarcode[], Err>({
    queryKey: catalogKeys.productBarcodes(productId ?? ''),
    queryFn: () => productsApi.getProductBarcodes(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

/**
 * Only the barcodes-list cache is invalidated on a barcode mutation — checked against
 * `ProductDetail`/`ProductSummary`/`ProductListResponse` (`model/types.ts`): none of them project
 * a barcode-derived field in this BFF contract, unlike variants/family-assignment which DO change
 * `ProductDetail` fields (`invalidateVariantQueries`/`onProductDetailChanged`, above). Revisit if a
 * future contract version surfaces a summary barcode on the product card or list row.
 */
export function useCreateProductBarcode() {
  const qc = useQueryClient();
  return useMutation<ProductBarcode, Err, { productId: string; body: ProductBarcodeCreateRequest }>({
    mutationFn: ({ productId, body }) => productsApi.createProductBarcode(productId, body),
    onSuccess: (_data, { productId }) =>
      void qc.invalidateQueries({ queryKey: catalogKeys.productBarcodes(productId) }),
  });
}

export function useSetPrimaryProductBarcode() {
  const qc = useQueryClient();
  return useMutation<void, Err, { productId: string; barcodeId: string }>({
    mutationFn: ({ productId, barcodeId }) => productsApi.setPrimaryProductBarcode(productId, barcodeId),
    onSuccess: (_data, { productId }) =>
      void qc.invalidateQueries({ queryKey: catalogKeys.productBarcodes(productId) }),
  });
}

export function useDeleteProductBarcode() {
  const qc = useQueryClient();
  return useMutation<void, Err, { productId: string; barcodeId: string }>({
    mutationFn: ({ productId, barcodeId }) => productsApi.deleteProductBarcode(productId, barcodeId),
    onSuccess: (_data, { productId }) =>
      void qc.invalidateQueries({ queryKey: catalogKeys.productBarcodes(productId) }),
  });
}

// ── Categories ──────────────────────────────────────────────────────────────
export function useCategories(
  req: CategoryListRequest = {},
  options?: Opts,
): UseQueryResult<CategoryListResponse, Err> {
  return useQuery<CategoryListResponse, Err>({
    queryKey: catalogKeys.categoriesList(req),
    queryFn: () => categoriesApi.listCategories(req),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCategory(
  categoryId: string | undefined,
  options?: Opts,
): UseQueryResult<Category, Err> {
  return useQuery<Category, Err>({
    queryKey: catalogKeys.category(categoryId ?? ''),
    queryFn: () => categoriesApi.getCategory(categoryId as string),
    enabled: (options?.enabled ?? true) && Boolean(categoryId),
  });
}

export function useUpsertCategory() {
  const qc = useQueryClient();
  return useMutation<ImportResult, Err, { categoryId: string; body: CategoryUpsertRequest }>({
    mutationFn: ({ categoryId, body }) => categoriesApi.upsertCategory(categoryId, body),
    onSuccess: (_data, { categoryId }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.categories });
      void qc.invalidateQueries({ queryKey: catalogKeys.categoryTranslations(categoryId) });
    },
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation<CategoryDetail, Err, CategoryUpsertRequest>({
    mutationFn: (body) => categoriesApi.createCategory(body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.categories });
      void qc.invalidateQueries({ queryKey: catalogKeys.categoryTranslations(data.id) });
    },
  });
}

/**
 * Every stored translation of a category (adr-tenant-005, plan-tenant-004 Wave 2) — feeds the
 * card-language switcher's per-locale drafts (`CategoryFormDialog`).
 */
export function useCategoryTranslations(
  categoryId: string | undefined,
  options?: Opts,
): UseQueryResult<CategoryTranslation[], Err> {
  return useQuery<CategoryTranslation[], Err>({
    queryKey: catalogKeys.categoryTranslations(categoryId ?? ''),
    queryFn: () => categoriesApi.getCategoryTranslations(categoryId as string),
    enabled: (options?.enabled ?? true) && Boolean(categoryId),
  });
}

/** Search master-catalog categories — epic-catalog-002, story-024. */
export function useSearchCategories(
  params: BrowseCategoriesParams = {},
  options?: Opts,
): UseQueryResult<CategorySummaryPage, Err> {
  return useQuery<CategorySummaryPage, Err>({
    queryKey: catalogKeys.categoriesSearch(params),
    queryFn: () => categoriesApi.searchCategories(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (categoryId) => categoriesApi.deleteCategory(categoryId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.categories }),
  });
}

/** Full tenant category tree (epic-catalog-005, user-story-catalog-011) — unpaginated. */
export function useCategoryTree(
  locale?: string,
  options?: Opts,
): UseQueryResult<CategoryTreeNode[], Err> {
  return useQuery<CategoryTreeNode[], Err>({
    queryKey: catalogKeys.categoryTree(locale),
    queryFn: () => categoriesApi.getCategoryTree(locale),
    enabled: options?.enabled ?? true,
  });
}

// ── Categories: version history (epic-catalog-005, user-story-merchant-portal-032) — list +
//    detail only; there is no category restore endpoint (unlike products). ─────────────────

export function useCategoryVersions(
  categoryId: string | undefined,
  params: { page: number; size: number },
  options?: Opts,
): UseQueryResult<CatalogVersionSummaryPage, Err> {
  return useQuery<CatalogVersionSummaryPage, Err>({
    queryKey: catalogKeys.categoryVersions(categoryId ?? '', params),
    queryFn: () => categoriesApi.getCategoryVersions(categoryId as string, params),
    enabled: (options?.enabled ?? true) && Boolean(categoryId),
    placeholderData: keepPreviousData,
  });
}

export function useCategoryVersion(
  categoryId: string | undefined,
  number: number | undefined,
  options?: Opts,
): UseQueryResult<CatalogVersionDetail, Err> {
  return useQuery<CatalogVersionDetail, Err>({
    queryKey: catalogKeys.categoryVersion(categoryId ?? '', number ?? 0),
    queryFn: () => categoriesApi.getCategoryVersion(categoryId as string, number as number),
    enabled: (options?.enabled ?? true) && Boolean(categoryId) && number !== undefined,
  });
}

// ── Media ─────────────────────────────────────────────────────────────────────
export function useUploadAsset() {
  return useMutation<AssetResponse, Err, UploadAssetParams>({
    mutationFn: (params) => mediaApi.uploadAsset(params),
  });
}

export function useCatalogMediaAssets(
  params: { ownerType: MediaOwnerType; ownerId?: string; visibility?: MediaVisibility },
  options?: Opts,
): UseQueryResult<AssetResponse[], Err> {
  return useQuery<AssetResponse[], Err>({
    queryKey: catalogKeys.mediaAssets(params),
    queryFn: () => mediaApi.listAssets(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useDeleteAsset() {
  return useMutation<void, Err, string>({ mutationFn: (assetId) => mediaApi.deleteAsset(assetId) });
}

// ── Import ──────────────────────────────────────────────────────────────────
export function useDownloadTemplate() {
  return useMutation<void, Err, void>({ mutationFn: () => importApi.downloadCatalogTemplate() });
}

export function useImportFile() {
  return useMutation<ImportRun, Err, { file: File; dryRun: boolean }>({
    mutationFn: ({ file, dryRun }) => importApi.importCatalogFile(file, dryRun),
  });
}

export function useGoogleSheetSettings(options?: Opts): UseQueryResult<GoogleSheetSettings, Err> {
  return useQuery<GoogleSheetSettings, Err>({
    queryKey: catalogKeys.googleSheet,
    queryFn: importApi.getGoogleSheetSettings,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpdateGoogleSheetSettings() {
  const qc = useQueryClient();
  return useMutation<GoogleSheetSettings, Err, GoogleSheetSettingsRequest>({
    mutationFn: importApi.updateGoogleSheetSettings,
    onSuccess: (data) => qc.setQueryData(catalogKeys.googleSheet, data),
  });
}

export function useImportFromGoogleSheet() {
  return useMutation<ImportRun, Err, { dryRun: boolean }>({
    mutationFn: ({ dryRun }) => importApi.importFromGoogleSheet(dryRun),
  });
}

// ── Import: custom feed (catalog-import-service) ─────────────────────────────
export function useUploadCatalogFeedFile() {
  return useMutation<CatalogFeedFile, Err, File>({
    mutationFn: (file) => feedImportApi.uploadCatalogFeedFile(file),
  });
}

/** Polls while the downstream is still processing asynchronously; stops once terminal. */
export function useCatalogFeedFile(
  fileId: string | undefined,
  options?: Opts,
): UseQueryResult<CatalogFeedFile, Err> {
  return useQuery<CatalogFeedFile, Err>({
    queryKey: catalogKeys.catalogFeedFile(fileId ?? ''),
    queryFn: () => feedImportApi.getCatalogFeedFile(fileId as string),
    enabled: (options?.enabled ?? true) && Boolean(fileId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'UPLOADED' || status === 'PROCESSING' ? 3000 : false;
    },
  });
}

export function useReprocessCatalogFeedFile() {
  return useMutation<CatalogFeedFile, Err, string>({
    mutationFn: (fileId) => feedImportApi.reprocessCatalogFeedFile(fileId),
  });
}

export function useExportCatalogFeedProduct() {
  return useMutation<void, Err, string>({
    mutationFn: (productArticle) => feedImportApi.exportCatalogFeedProduct(productArticle),
  });
}

export function useExportAllCatalogFeedProducts() {
  return useMutation<void, Err, void>({
    mutationFn: () => feedImportApi.exportAllCatalogFeedProducts(),
  });
}

/** 404 means "not configured yet" — an expected state, not an error (mirrors usePriceProtectionPolicy). */
export function useCatalogFeedColumnMapping(options?: Opts): UseQueryResult<CatalogFeedColumnMapping, Err> {
  return useQuery<CatalogFeedColumnMapping, Err>({
    queryKey: catalogKeys.catalogFeedColumnMapping,
    queryFn: feedImportApi.getCatalogFeedColumnMapping,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useCreateCatalogFeedColumnMapping() {
  const qc = useQueryClient();
  return useMutation<CatalogFeedColumnMapping, Err, CatalogFeedColumnMappingRequest>({
    mutationFn: feedImportApi.createCatalogFeedColumnMapping,
    onSuccess: (data) => qc.setQueryData(catalogKeys.catalogFeedColumnMapping, data),
  });
}

export function useUpdateCatalogFeedColumnMapping() {
  const qc = useQueryClient();
  return useMutation<CatalogFeedColumnMapping, Err, CatalogFeedColumnMappingRequest>({
    mutationFn: feedImportApi.updateCatalogFeedColumnMapping,
    onSuccess: (data) => qc.setQueryData(catalogKeys.catalogFeedColumnMapping, data),
  });
}

// ── Pricing: runtime calculations ───────────────────────────────────────────
export function useProductPrice(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<ProductPriceResponse, Err> {
  return useQuery<ProductPriceResponse, Err>({
    queryKey: catalogKeys.productPrice(productId ?? ''),
    queryFn: () => pricingApi.getProductPrice(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
    retry: false,
  });
}

export function useCalculateBasketPricing() {
  return useMutation<BasketPricingResponse, Err, BasketPricingRequest>({
    mutationFn: pricingApi.calculateBasketPricing,
  });
}

export function useCalculateCheckoutPricing() {
  return useMutation<CheckoutPricingResponse, Err, CheckoutPricingRequest>({
    mutationFn: pricingApi.calculateCheckoutPricing,
  });
}

export function useValidatePromoPricing() {
  return useMutation<PromoValidationResponse, Err, PromoValidationRequest>({
    mutationFn: pricingApi.validatePromoPricing,
  });
}

export function useCreatePricingQuote() {
  return useMutation<PricingQuoteResponse, Err, CheckoutPricingRequest>({
    mutationFn: pricingApi.createPricingQuote,
  });
}

export function usePricingQuote(
  quoteId: string | undefined,
  options?: Opts,
): UseQueryResult<PricingQuoteResponse, Err> {
  return useQuery<PricingQuoteResponse, Err>({
    queryKey: catalogKeys.pricingQuote(quoteId ?? ''),
    queryFn: () => pricingApi.getPricingQuote(quoteId as string),
    enabled: (options?.enabled ?? true) && Boolean(quoteId),
    retry: false,
  });
}

export function useCreatePricingSnapshot() {
  const qc = useQueryClient();
  return useMutation<PricingSnapshotResponse, Err, CreatePricingSnapshotRequest>({
    mutationFn: pricingApi.createPricingSnapshot,
    onSuccess: (data) => qc.setQueryData(catalogKeys.pricingSnapshot(data.orderId), data),
  });
}

export function usePricingSnapshotByOrderId(
  orderId: string | undefined,
  options?: Opts,
): UseQueryResult<PricingSnapshotResponse, Err> {
  return useQuery<PricingSnapshotResponse, Err>({
    queryKey: catalogKeys.pricingSnapshot(orderId ?? ''),
    queryFn: () => pricingApi.getPricingSnapshotByOrderId(orderId as string),
    enabled: (options?.enabled ?? true) && Boolean(orderId),
    retry: false,
  });
}

export function usePricingAuditLog(
  params: { customerId?: string; page?: number; size?: number },
  options?: Opts,
): UseQueryResult<PricingAuditLogPage, Err> {
  return useQuery<PricingAuditLogPage, Err>({
    queryKey: catalogKeys.pricingAuditLog(params),
    queryFn: () => pricingApi.getPricingAuditLog(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

// ── Pricing: config ─────────────────────────────────────────────────────────
export function usePricingConfig(options?: Opts): UseQueryResult<TenantPricingConfigResponse, Err> {
  return useQuery<TenantPricingConfigResponse, Err>({
    queryKey: catalogKeys.pricingConfig,
    queryFn: pricingApi.getPricingConfig,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpsertPricingConfig() {
  const qc = useQueryClient();
  return useMutation<TenantPricingConfigResponse, Err, TenantPricingConfigRequest>({
    mutationFn: pricingApi.upsertPricingConfig,
    onSuccess: (data) => qc.setQueryData(catalogKeys.pricingConfig, data),
  });
}

// ── Pricing: rules ────────────────────────────────────────────────────────────
export function usePricingRules(
  params: ListPricingRulesParams,
  options?: Opts,
): UseQueryResult<PricingRulePage, Err> {
  return useQuery<PricingRulePage, Err>({
    queryKey: catalogKeys.pricingRules(params),
    queryFn: () => pricingApi.listPricingRules(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCreatePricingRule() {
  const qc = useQueryClient();
  return useMutation<PricingRuleResponse, Err, PricingRuleRequest>({
    mutationFn: pricingApi.createPricingRule,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

export function useUpdatePricingRule() {
  const qc = useQueryClient();
  return useMutation<PricingRuleResponse, Err, { ruleId: string; body: PricingRuleUpdateRequest }>({
    mutationFn: ({ ruleId, body }) => pricingApi.updatePricingRule(ruleId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

export function useDeactivatePricingRule() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (ruleId) => pricingApi.deactivatePricingRule(ruleId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

// ── Pricing: provider configs ─────────────────────────────────────────────────
export function useProviderConfigs(
  options?: Opts,
): UseQueryResult<ExternalProviderConfigResponse[], Err> {
  return useQuery<ExternalProviderConfigResponse[], Err>({
    queryKey: catalogKeys.providerConfigs,
    queryFn: pricingApi.listProviderConfigs,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateProviderConfig() {
  const qc = useQueryClient();
  return useMutation<ExternalProviderConfigResponse, Err, ExternalProviderConfigRequest>({
    mutationFn: pricingApi.createProviderConfig,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.providerConfigs }),
  });
}

export function useDeactivateProviderConfig() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (configId) => pricingApi.deactivateProviderConfig(configId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.providerConfigs }),
  });
}

export function useProviderCredentialStatus(
  configId: string | undefined,
  options?: Opts,
): UseQueryResult<ProviderCredentialStatusResponse, Err> {
  return useQuery<ProviderCredentialStatusResponse, Err>({
    queryKey: catalogKeys.providerCredential(configId ?? ''),
    queryFn: () => pricingApi.getProviderCredentialStatus(configId as string),
    enabled: (options?.enabled ?? true) && Boolean(configId),
    retry: false,
  });
}

export function useSetProviderCredential() {
  const qc = useQueryClient();
  return useMutation<void, Err, { configId: string; body: ProviderCredentialRequest }>({
    mutationFn: ({ configId, body }) => pricingApi.setProviderCredential(configId, body),
    onSuccess: (_data, { configId }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.providerCredential(configId) });
    },
  });
}

// ── Pricing: approvals ────────────────────────────────────────────────────────
export function usePriceApprovals(
  params: { page?: number; size?: number },
  options?: Opts,
): UseQueryResult<GovernedPricePage, Err> {
  return useQuery<GovernedPricePage, Err>({
    queryKey: catalogKeys.approvals(params),
    queryFn: () => pricingApi.listPriceApprovals(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useApprovePrice() {
  const qc = useQueryClient();
  return useMutation<GovernedPriceResponse, Err, string>({
    mutationFn: (priceId) => pricingApi.approvePrice(priceId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

export function useRejectPrice() {
  const qc = useQueryClient();
  return useMutation<GovernedPriceResponse, Err, { priceId: string; reason?: string }>({
    mutationFn: ({ priceId, reason }) => pricingApi.rejectPrice(priceId, { reason }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.pricing }),
  });
}

// ── Pricing: protection policy + audit ─────────────────────────────────────────
export function usePriceProtectionPolicy(
  options?: Opts,
): UseQueryResult<PriceProtectionPolicyResponse, Err> {
  return useQuery<PriceProtectionPolicyResponse, Err>({
    queryKey: catalogKeys.protectionPolicy,
    queryFn: pricingApi.getPriceProtectionPolicy,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpsertPriceProtectionPolicy() {
  const qc = useQueryClient();
  return useMutation<PriceProtectionPolicyResponse, Err, PriceProtectionPolicyRequest>({
    mutationFn: pricingApi.upsertPriceProtectionPolicy,
    onSuccess: (data) => qc.setQueryData(catalogKeys.protectionPolicy, data),
  });
}

export function usePriceLifecycleAudit(
  params: { productId?: string; page?: number; size?: number },
  options?: Opts,
): UseQueryResult<PriceLifecycleAuditPage, Err> {
  return useQuery<PriceLifecycleAuditPage, Err>({
    queryKey: catalogKeys.audit(params),
    queryFn: () => pricingApi.getPriceLifecycleAudit(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

// ── Pricing: sales channels (Phase 2b/3) ─────────────────────────────────────
export function useSalesChannels(options?: Opts): UseQueryResult<SalesChannelResponse[], Err> {
  return useQuery<SalesChannelResponse[], Err>({
    queryKey: catalogKeys.salesChannels,
    queryFn: pricingApi.listSalesChannels,
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateSalesChannel() {
  const qc = useQueryClient();
  return useMutation<SalesChannelResponse, Err, { code: string; body: SalesChannelUpdateRequest }>({
    mutationFn: ({ code, body }) => pricingApi.updateSalesChannel(code, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.salesChannels }),
  });
}

// ── Pricing: price lines (Phase 2b/3) ─────────────────────────────────────────
export function useProductPriceLines(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<PriceLineResponse[], Err> {
  return useQuery<PriceLineResponse[], Err>({
    queryKey: catalogKeys.priceLines(productId ?? ''),
    queryFn: () => pricingApi.listPriceLines(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

export function useSubmitPriceLine() {
  const qc = useQueryClient();
  return useMutation<PriceLineResponse, Err, PriceLineRequest>({
    mutationFn: pricingApi.submitPriceLine,
    onSuccess: (line) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.priceLines(line.productId) });
      void qc.invalidateQueries({ queryKey: catalogKeys.products });
      void qc.invalidateQueries({ queryKey: catalogKeys.approvals({}) });
      // List-context save (design spec §8.3/§8.6) — drop the list's own lines/summary cache too.
      if (line.priceListCode) {
        void qc.invalidateQueries({ queryKey: catalogKeys.priceList(line.priceListCode) });
      }
    },
  });
}

// ── Pricing: price resolution (Phase 2b/3) ───────────────────────────────────
export function usePriceResolution(
  params: ResolvePriceParams | undefined,
  options?: Opts,
): UseQueryResult<ResolvedPriceResponse, Err> {
  return useQuery<ResolvedPriceResponse, Err>({
    queryKey: catalogKeys.priceResolution(params ?? { productId: '' }),
    queryFn: () => pricingApi.resolvePrice(params as ResolvePriceParams),
    enabled: (options?.enabled ?? true) && Boolean(params?.productId),
  });
}

// ── Price lists (adr-pricing-025, plan-platform-002 WP4.2) ───────────────────
export function usePriceLists(options?: Opts): UseQueryResult<PriceListResponse[], Err> {
  return useQuery<PriceListResponse[], Err>({
    queryKey: catalogKeys.priceLists,
    queryFn: priceListsApi.listPriceLists,
    enabled: options?.enabled ?? true,
  });
}

export function usePriceList(
  code: string | undefined,
  options?: Opts,
): UseQueryResult<PriceListResponse, Err> {
  return useQuery<PriceListResponse, Err>({
    queryKey: catalogKeys.priceList(code ?? ''),
    queryFn: () => priceListsApi.getPriceList(code as string),
    enabled: (options?.enabled ?? true) && Boolean(code),
  });
}

export function useCreatePriceList() {
  const qc = useQueryClient();
  return useMutation<PriceListResponse, Err, PriceListRequest>({
    mutationFn: priceListsApi.createPriceList,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.priceLists }),
  });
}

/** Also (de)activates the list — `active` rides the same PUT (adr-pricing-025 d4). */
export function useUpdatePriceList() {
  const qc = useQueryClient();
  return useMutation<PriceListResponse, Err, { code: string; body: PriceListRequest }>({
    mutationFn: ({ code, body }) => priceListsApi.updatePriceList(code, body),
    onSuccess: (updated) => {
      qc.setQueryData(catalogKeys.priceList(updated.code), updated);
      void qc.invalidateQueries({ queryKey: catalogKeys.priceLists });
    },
  });
}

export function usePriceListBindings(
  code: string | undefined,
  options?: Opts,
): UseQueryResult<PriceListBindingResponse[], Err> {
  return useQuery<PriceListBindingResponse[], Err>({
    queryKey: catalogKeys.priceListBindings(code ?? ''),
    queryFn: () => priceListsApi.listPriceListBindings(code as string),
    enabled: (options?.enabled ?? true) && Boolean(code),
  });
}

export function useBindPriceListStore() {
  const qc = useQueryClient();
  return useMutation<PriceListBindingResponse, Err, { code: string; storeCode: string }>({
    mutationFn: ({ code, storeCode }) => priceListsApi.bindPriceListStore(code, storeCode),
    onSuccess: (_binding, { code }) =>
      void qc.invalidateQueries({ queryKey: catalogKeys.priceList(code) }),
  });
}

export function useUnbindPriceListStore() {
  const qc = useQueryClient();
  return useMutation<void, Err, { code: string; storeCode: string }>({
    mutationFn: ({ code, storeCode }) => priceListsApi.unbindPriceListStore(code, storeCode),
    onSuccess: (_result, { code }) =>
      void qc.invalidateQueries({ queryKey: catalogKeys.priceList(code) }),
  });
}

/** See `catalogPriceListsApi.ts`'s header comment — a disclosed contract gap, coded against the
 * target shape (`GET .../price-lines?priceListCode=`), so the list-detail lines grid lights up
 * with zero further changes once the BFF ships the filter. */
export function usePriceListLines(
  params: ListPriceListLinesParams | undefined,
  options?: Opts,
): UseQueryResult<PriceLineResponse[], Err> {
  return useQuery<PriceLineResponse[], Err>({
    queryKey: catalogKeys.priceListLines(params?.priceListCode ?? '', {
      page: params?.page,
      size: params?.size,
    }),
    queryFn: () => priceListsApi.listPriceListLines(params as ListPriceListLinesParams),
    enabled: (options?.enabled ?? true) && Boolean(params?.priceListCode),
    retry: false,
  });
}

/** design spec §3/A3 — the retail-store directory (every store, bound or not). */
export function useRetailStores(options?: Opts): UseQueryResult<RetailStoreOption[], Err> {
  return useQuery<RetailStoreOption[], Err>({
    queryKey: catalogKeys.retailStores,
    queryFn: priceListsApi.listRetailStores,
    enabled: options?.enabled ?? true,
  });
}

export interface PriceListsOverview {
  lists: PriceListResponse[];
  bindingsByCode: Map<string, PriceListBindingResponse[]>;
  /** Best-effort — absent for a list whose lines fetch is still pending or failed (see the gap note above). */
  linesByCode: Map<string, PriceLineResponse[]>;
  linesLoadingCodes: Set<string>;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Composes the price-lists table's Lists sub-view (§8.1) and its Store-coverage sub-view (§8.4)
 * from the SAME underlying fetch — every list's own bindings + best-effort lines, via
 * `useQueries` (the `useBudgetsForIds`/`usePromotionsForIds` pattern from the Promo workspace),
 * bounded by the tenant's own (small) price-list count, never by store/product count. A lines
 * fetch failing (the disclosed contract gap) never fails the whole overview — it only leaves that
 * one list's `linesCount`/`coveragePercent` at "—" (`lib/priceListCoverage.ts` renders `null`
 * that way), so `isError` deliberately tracks only the lists/bindings queries.
 */
export function usePriceListsOverview(options?: Opts): PriceListsOverview {
  const listsQuery = usePriceLists(options);
  const codes = (listsQuery.data ?? []).map((list) => list.code);

  const bindingsQueries = useQueries({
    queries: codes.map((code) => ({
      queryKey: catalogKeys.priceListBindings(code),
      queryFn: () => priceListsApi.listPriceListBindings(code),
      enabled: options?.enabled ?? true,
    })),
  });
  const linesQueries = useQueries({
    queries: codes.map((code) => ({
      queryKey: catalogKeys.priceListLines(code, {}),
      queryFn: () => priceListsApi.listPriceListLines({ priceListCode: code }),
      enabled: options?.enabled ?? true,
      retry: false,
    })),
  });

  const bindingsByCode = new Map<string, PriceListBindingResponse[]>();
  const linesByCode = new Map<string, PriceLineResponse[]>();
  const linesLoadingCodes = new Set<string>();
  codes.forEach((code, index) => {
    const bindings = bindingsQueries[index];
    if (bindings?.data) bindingsByCode.set(code, bindings.data);
    const lines = linesQueries[index];
    if (lines?.data) linesByCode.set(code, lines.data);
    else if (lines?.isLoading) linesLoadingCodes.add(code);
  });

  return {
    lists: listsQuery.data ?? [],
    bindingsByCode,
    linesByCode,
    linesLoadingCodes,
    isLoading: listsQuery.isLoading || bindingsQueries.some((q) => q.isLoading),
    isError: listsQuery.isError || bindingsQueries.some((q) => q.isError),
    refetch: () => {
      void listsQuery.refetch();
      bindingsQueries.forEach((q) => void q.refetch());
      linesQueries.forEach((q) => void q.refetch());
    },
  };
}

export interface StoreListBindings {
  lists: PriceListResponse[];
  bindingsByCode: Map<string, PriceListBindingResponse[]>;
  isLoading: boolean;
}

/**
 * Store→bound-active-price-list lookup (design spec §8.12/§8.13, plan-merchant-portal-018 WP2.3/
 * WP2.4) — lists + their bindings ONLY, deliberately lighter than {@link usePriceListsOverview}:
 * that composition ALSO fires one best-effort `GET price-lines?priceListCode=` per list (for its
 * own coverage/staleness numbers), which is a disclosed contract gap that 400s on the live BFF
 * today (`catalogPriceListsApi.ts`'s header) — harmless there (tolerated via `retry: false` and an
 * `isError` that ignores it) but pure waste for a caller that only needs "which list, if any, is
 * this store bound to" (the products list's own scope-declared fallback / bulk-target derivation
 * never needs a list's lines). Shares `catalogKeys.priceListBindings` with `usePriceListsOverview`,
 * so the two compositions' cache entries stay coherent wherever both are mounted at once.
 */
export function useStoreListBindings(options?: Opts): StoreListBindings {
  const listsQuery = usePriceLists(options);
  const codes = (listsQuery.data ?? []).map((list) => list.code);

  const bindingsQueries = useQueries({
    queries: codes.map((code) => ({
      queryKey: catalogKeys.priceListBindings(code),
      queryFn: () => priceListsApi.listPriceListBindings(code),
      enabled: options?.enabled ?? true,
    })),
  });

  const bindingsByCode = new Map<string, PriceListBindingResponse[]>();
  codes.forEach((code, index) => {
    const bindings = bindingsQueries[index];
    if (bindings?.data) bindingsByCode.set(code, bindings.data);
  });

  return {
    lists: listsQuery.data ?? [],
    bindingsByCode,
    isLoading: listsQuery.isLoading || bindingsQueries.some((q) => q.isLoading),
  };
}

export function useDiscountRoundingPolicy(
  options?: Opts,
): UseQueryResult<DiscountRoundingPolicyResponse, Err> {
  return useQuery<DiscountRoundingPolicyResponse, Err>({
    queryKey: catalogKeys.discountRoundingPolicy,
    queryFn: pricingApi.getDiscountRoundingPolicy,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function usePutDiscountRoundingPolicy() {
  const qc = useQueryClient();
  return useMutation<DiscountRoundingPolicyResponse, Err, DiscountRoundingPolicyRequest>({
    mutationFn: pricingApi.putDiscountRoundingPolicy,
    onSuccess: (data) => qc.setQueryData(catalogKeys.discountRoundingPolicy, data),
  });
}

// ── Rounding policies (adr-pricing-022, epic-pricing-002 Phase 1, WP4.3) ─────
export function useRoundingPolicies(options?: Opts): UseQueryResult<RoundingPolicyResponse[], Err> {
  return useQuery<RoundingPolicyResponse[], Err>({
    queryKey: catalogKeys.roundingPolicies,
    queryFn: pricingApi.listRoundingPolicies,
    enabled: options?.enabled ?? true,
  });
}

export function usePutRoundingPolicy() {
  const qc = useQueryClient();
  return useMutation<RoundingPolicyResponse, Err, RoundingPolicyRequest>({
    mutationFn: pricingApi.putRoundingPolicy,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.roundingPolicies }),
  });
}

// ── Customer groups (epic-pricing-002 Phase 1, adr-pricing-009, WP4.3) ───────
export function useCustomerGroups(options?: Opts): UseQueryResult<CustomerGroupResponse[], Err> {
  return useQuery<CustomerGroupResponse[], Err>({
    queryKey: catalogKeys.customerGroups,
    queryFn: customerGroupsApi.listCustomerGroups,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateCustomerGroup() {
  const qc = useQueryClient();
  return useMutation<CustomerGroupResponse, Err, CustomerGroupRequest>({
    mutationFn: customerGroupsApi.createCustomerGroup,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.customerGroups }),
  });
}

/** No `GET .../customer-groups/{code}` exists (see `catalogCustomerGroupsApi.ts`'s header) — the
 * caller always resends the group's own existing `code` unchanged; the UI never offers to edit it. */
export function useUpdateCustomerGroup() {
  const qc = useQueryClient();
  return useMutation<CustomerGroupResponse, Err, { code: string; body: CustomerGroupRequest }>({
    mutationFn: ({ code, body }) => customerGroupsApi.updateCustomerGroup(code, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.customerGroups }),
  });
}

export function useCustomerGroupMembers(
  code: string | undefined,
  options?: Opts,
): UseQueryResult<CustomerGroupMemberResponse[], Err> {
  return useQuery<CustomerGroupMemberResponse[], Err>({
    queryKey: catalogKeys.customerGroupMembers(code ?? ''),
    queryFn: () => customerGroupsApi.listCustomerGroupMembers(code as string),
    enabled: (options?.enabled ?? true) && Boolean(code),
  });
}

export function useAddCustomerGroupMember() {
  const qc = useQueryClient();
  return useMutation<void, Err, { code: string; body: CustomerGroupMemberRequest }>({
    mutationFn: ({ code, body }) => customerGroupsApi.addCustomerGroupMember(code, body),
    onSuccess: (_result, { code }) =>
      void qc.invalidateQueries({ queryKey: catalogKeys.customerGroupMembers(code) }),
  });
}

export function useRemoveCustomerGroupMember() {
  const qc = useQueryClient();
  return useMutation<void, Err, { code: string; member: CustomerGroupMemberRequest }>({
    mutationFn: ({ code, member }) => customerGroupsApi.removeCustomerGroupMember(code, member),
    onSuccess: (_result, { code }) =>
      void qc.invalidateQueries({ queryKey: catalogKeys.customerGroupMembers(code) }),
  });
}

export interface CustomerGroupsOverview {
  groups: CustomerGroupResponse[];
  /** STATIC groups only — DYNAMIC has no member list (design spec §8.10). */
  memberCountByCode: Map<string, number>;
  memberCountLoadingCodes: Set<string>;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Composes the groups table's member-count column (design spec §8.10) from the groups list plus
 * each STATIC group's own member list, via `useQueries` — the SAME `usePriceListsOverview`/
 * `usePriceLinesForProducts` batching pattern, bounded by the tenant's own (small) group count.
 */
export function useCustomerGroupsOverview(options?: Opts): CustomerGroupsOverview {
  const groupsQuery = useCustomerGroups(options);
  const staticCodes = (groupsQuery.data ?? []).filter((g) => g.kind === 'STATIC').map((g) => g.code);

  const memberQueries = useQueries({
    queries: staticCodes.map((code) => ({
      queryKey: catalogKeys.customerGroupMembers(code),
      queryFn: () => customerGroupsApi.listCustomerGroupMembers(code),
      enabled: options?.enabled ?? true,
    })),
  });

  const memberCountByCode = new Map<string, number>();
  const memberCountLoadingCodes = new Set<string>();
  staticCodes.forEach((code, index) => {
    const query = memberQueries[index];
    if (query?.data) memberCountByCode.set(code, query.data.length);
    else if (query?.isLoading) memberCountLoadingCodes.add(code);
  });

  return {
    groups: groupsQuery.data ?? [],
    memberCountByCode,
    memberCountLoadingCodes,
    isLoading: groupsQuery.isLoading,
    isError: groupsQuery.isError,
    refetch: () => {
      void groupsQuery.refetch();
      memberQueries.forEach((q) => void q.refetch());
    },
  };
}

// ── Bulk repricing batches (epic-pricing-002 Phase 1, WP4.3) ────────────────
export function usePriceChangeBatches(
  params: ListPriceChangeBatchesParams,
  options?: Opts,
): UseQueryResult<PriceChangeBatchResponse[], Err> {
  return useQuery<PriceChangeBatchResponse[], Err>({
    queryKey: catalogKeys.priceChangeBatches(params),
    queryFn: () => batchesApi.listPriceChangeBatches(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCreatePriceChangeBatch() {
  const qc = useQueryClient();
  return useMutation<PriceChangeBatchResponse, Err, CreatePriceChangeBatchRequest>({
    mutationFn: batchesApi.createPriceChangeBatch,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.priceChangeBatches({}) }),
  });
}

/** Polls while the batch is applying asynchronously; stops once terminal (mirrors
 * `useCatalogFeedFile`'s import-processing poll). */
export function usePriceChangeBatch(
  batchId: string | undefined,
  options?: Opts,
): UseQueryResult<PriceChangeBatchDetailResponse, Err> {
  return useQuery<PriceChangeBatchDetailResponse, Err>({
    queryKey: catalogKeys.priceChangeBatch(batchId ?? ''),
    queryFn: () => batchesApi.getPriceChangeBatch(batchId as string),
    enabled: (options?.enabled ?? true) && Boolean(batchId),
    refetchInterval: (query) => (query.state.data?.batch.status === 'APPLYING' ? 3000 : false),
  });
}

export function useDryRunPriceChangeBatch() {
  const qc = useQueryClient();
  return useMutation<PriceChangeBatchDetailResponse, Err, string>({
    mutationFn: batchesApi.dryRunPriceChangeBatch,
    onSuccess: (detail) => {
      qc.setQueryData(catalogKeys.priceChangeBatch(detail.batch.id), detail);
      void qc.invalidateQueries({ queryKey: catalogKeys.priceChangeBatches({}) });
    },
  });
}

export function useApplyPriceChangeBatch() {
  const qc = useQueryClient();
  return useMutation<PriceChangeBatchDetailResponse, Err, string>({
    mutationFn: batchesApi.applyPriceChangeBatch,
    onSuccess: (detail) => {
      qc.setQueryData(catalogKeys.priceChangeBatch(detail.batch.id), detail);
      void qc.invalidateQueries({ queryKey: catalogKeys.priceChangeBatches({}) });
      // A batch that lands part- or fully approved-pending affects the approval queue too.
      void qc.invalidateQueries({ queryKey: catalogKeys.approvals({}) });
      void qc.invalidateQueries({ queryKey: catalogKeys.products });
    },
  });
}

// ── Markdown plans (epic-pricing-002 Phase 1, WP4.3) ─────────────────────────
export function useMarkdownPlans(
  params: ListMarkdownPlansParams,
  options?: Opts,
): UseQueryResult<MarkdownPlanResponse[], Err> {
  return useQuery<MarkdownPlanResponse[], Err>({
    queryKey: catalogKeys.markdownPlans(params),
    queryFn: () => markdownPlansApi.listMarkdownPlans(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCreateMarkdownPlan() {
  const qc = useQueryClient();
  return useMutation<MarkdownPlanResponse, Err, CreateMarkdownPlanRequest>({
    mutationFn: markdownPlansApi.createMarkdownPlan,
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.markdownPlans({}) }),
  });
}

export function useMarkdownPlan(
  planId: string | undefined,
  options?: Opts,
): UseQueryResult<MarkdownPlanDetailResponse, Err> {
  return useQuery<MarkdownPlanDetailResponse, Err>({
    queryKey: catalogKeys.markdownPlan(planId ?? ''),
    queryFn: () => markdownPlansApi.getMarkdownPlan(planId as string),
    enabled: (options?.enabled ?? true) && Boolean(planId),
  });
}

export function useActivateMarkdownPlan() {
  const qc = useQueryClient();
  return useMutation<MarkdownPlanResponse, Err, string>({
    mutationFn: markdownPlansApi.activateMarkdownPlan,
    onSuccess: (plan) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.markdownPlan(plan.id) });
      void qc.invalidateQueries({ queryKey: catalogKeys.markdownPlans({}) });
    },
  });
}

export function useCancelMarkdownPlan() {
  const qc = useQueryClient();
  return useMutation<MarkdownPlanResponse, Err, string>({
    mutationFn: markdownPlansApi.cancelMarkdownPlan,
    onSuccess: (plan) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.markdownPlan(plan.id) });
      void qc.invalidateQueries({ queryKey: catalogKeys.markdownPlans({}) });
    },
  });
}

// ── Price grid: batched per-page price-line fetch (design spec §8.5, R5 fix, WP4.3) ─────────
export interface PriceLinesForProducts {
  linesByProductId: Map<string, PriceLineResponse[]>;
  isLoading: boolean;
}

/**
 * Replaces the price grid's per-row `useProductPriceLines` call (one `useQuery` per rendered
 * row, unbounded before this page gained pagination) with ONE `useQueries` composition scoped to
 * the CURRENT PAGE's product ids — the same `usePriceListsOverview` batching pattern. The BFF has
 * no batch price-lines endpoint, so this still issues one `GET price-lines?productId=` per
 * product; what changes is the fan-out is now bounded to the page size and the panel gets one
 * aggregate loading flag plus the full per-product data it needs to derive tier columns, instead
 * of each row owning an independent query. Shares its query key with `useProductPriceLines`
 * (`catalogKeys.priceLines`), so the cache stays coherent whichever hook a screen uses.
 */
export function usePriceLinesForProducts(
  productIds: string[],
  options?: Opts,
): PriceLinesForProducts {
  const queries = useQueries({
    queries: productIds.map((id) => ({
      queryKey: catalogKeys.priceLines(id),
      queryFn: () => pricingApi.listPriceLines(id),
      enabled: options?.enabled ?? true,
    })),
  });

  const linesByProductId = new Map<string, PriceLineResponse[]>();
  productIds.forEach((id, index) => {
    const data = queries[index]?.data;
    if (data) linesByProductId.set(id, data);
  });

  return {
    linesByProductId,
    isLoading: queries.some((q) => q.isLoading),
  };
}

// ── Product-360 stock panel (ProductEditPage) ────────────────────────────────
export function useProductStock(
  productId: string | undefined,
  options?: Opts,
): UseQueryResult<ProductStockItemListResponse, Err> {
  return useQuery<ProductStockItemListResponse, Err>({
    queryKey: catalogKeys.productStock(productId ?? ''),
    queryFn: () => productStockApi.getProductStock(productId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId),
  });
}

export function useProductStockLocations(
  options?: Opts,
): UseQueryResult<ProductStockLocation[], Err> {
  return useQuery<ProductStockLocation[], Err>({
    queryKey: catalogKeys.productStockLocations,
    queryFn: productStockApi.searchAllLocations,
    enabled: options?.enabled ?? true,
    // Locations rarely change; a longer staleTime avoids a refetch on every panel mount.
    staleTime: 5 * 60_000,
  });
}

// ── Catalog languages (epic-catalog-004, user-story-catalog-006) ────────────
export function useCatalogLanguages(options?: Opts): UseQueryResult<CatalogLanguage[], Err> {
  return useQuery<CatalogLanguage[], Err>({
    queryKey: catalogKeys.languages,
    queryFn: languagesApi.listCatalogLanguages,
    enabled: options?.enabled ?? true,
  });
}

export function useAddCatalogLanguage() {
  const qc = useQueryClient();
  return useMutation<CatalogLanguage, Err, CatalogLanguageCreateRequest>({
    mutationFn: (body) => languagesApi.addCatalogLanguage(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.languages }),
  });
}

export function useUpdateCatalogLanguage() {
  const qc = useQueryClient();
  return useMutation<CatalogLanguage, Err, { locale: string; body: CatalogLanguageUpdateRequest }>({
    mutationFn: ({ locale, body }) => languagesApi.updateCatalogLanguage(locale, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.languages }),
  });
}

export function useDeleteCatalogLanguage() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (locale) => languagesApi.deleteCatalogLanguage(locale),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.languages }),
  });
}

// ── Attribute dictionary (epic-catalog-004/005) ──────────────────────────────
export function useAttributes(options?: Opts): UseQueryResult<AttributeDefinition[], Err> {
  return useQuery<AttributeDefinition[], Err>({
    queryKey: catalogKeys.attributes,
    queryFn: attributesApi.listAttributes,
    enabled: options?.enabled ?? true,
  });
}

export function useAttribute(
  code: string | undefined,
  options?: Opts,
): UseQueryResult<AttributeDefinition, Err> {
  return useQuery<AttributeDefinition, Err>({
    queryKey: catalogKeys.attribute(code ?? ''),
    queryFn: () => attributesApi.getAttribute(code as string),
    enabled: (options?.enabled ?? true) && Boolean(code),
  });
}

export function useCreateAttribute() {
  const qc = useQueryClient();
  return useMutation<AttributeDefinition, Err, AttributeCreateRequest>({
    mutationFn: (body) => attributesApi.createAttribute(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.attributes }),
  });
}

export function useUpdateAttribute() {
  const qc = useQueryClient();
  return useMutation<AttributeDefinition, Err, { code: string; body: AttributeUpdateRequest }>({
    mutationFn: ({ code, body }) => attributesApi.updateAttribute(code, body),
    onSuccess: (data) => {
      qc.setQueryData(catalogKeys.attribute(data.code), data);
      void qc.invalidateQueries({ queryKey: catalogKeys.attributes });
    },
  });
}

export function useDeleteAttribute() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (code) => attributesApi.deleteAttribute(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.attributes }),
  });
}

export function useAddAttributeOption() {
  const qc = useQueryClient();
  return useMutation<AttributeOption, Err, { code: string; body: AttributeOptionInput }>({
    mutationFn: ({ code, body }) => attributesApi.addAttributeOption(code, body),
    onSuccess: (_data, { code }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.attribute(code) });
      void qc.invalidateQueries({ queryKey: catalogKeys.attributes });
    },
  });
}

export function useUpdateAttributeOption() {
  const qc = useQueryClient();
  return useMutation<
    AttributeOption,
    Err,
    { code: string; optionCode: string; body: AttributeOptionUpdateRequest }
  >({
    mutationFn: ({ code, optionCode, body }) =>
      attributesApi.updateAttributeOption(code, optionCode, body),
    onSuccess: (_data, { code }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.attribute(code) });
      void qc.invalidateQueries({ queryKey: catalogKeys.attributes });
    },
  });
}

export function useDeleteAttributeOption() {
  const qc = useQueryClient();
  return useMutation<void, Err, { code: string; optionCode: string }>({
    mutationFn: ({ code, optionCode }) => attributesApi.deleteAttributeOption(code, optionCode),
    onSuccess: (_data, { code }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.attribute(code) });
      void qc.invalidateQueries({ queryKey: catalogKeys.attributes });
    },
  });
}

// ── Attribute groups (plan-catalog-009, user-story-catalog-024) ─────────────
export function useAttributeGroups(options?: Opts): UseQueryResult<AttributeGroup[], Err> {
  return useQuery<AttributeGroup[], Err>({
    queryKey: catalogKeys.attributeGroups,
    queryFn: attributeGroupsApi.listAttributeGroups,
    enabled: options?.enabled ?? true,
  });
}

export function useAttributeGroup(
  code: string | undefined,
  options?: Opts,
): UseQueryResult<AttributeGroup, Err> {
  return useQuery<AttributeGroup, Err>({
    queryKey: catalogKeys.attributeGroup(code ?? ''),
    queryFn: () => attributeGroupsApi.getAttributeGroup(code as string),
    enabled: (options?.enabled ?? true) && Boolean(code),
  });
}

export function useCreateAttributeGroup() {
  const qc = useQueryClient();
  return useMutation<AttributeGroup, Err, AttributeGroupCreateRequest>({
    mutationFn: (body) => attributeGroupsApi.createAttributeGroup(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.attributeGroups }),
  });
}

export function useUpdateAttributeGroup() {
  const qc = useQueryClient();
  return useMutation<AttributeGroup, Err, { code: string; body: AttributeGroupUpdateRequest }>({
    mutationFn: ({ code, body }) => attributeGroupsApi.updateAttributeGroup(code, body),
    onSuccess: (data) => {
      qc.setQueryData(catalogKeys.attributeGroup(data.code), data);
      void qc.invalidateQueries({ queryKey: catalogKeys.attributeGroups });
    },
  });
}

export function useDeleteAttributeGroup() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (code) => attributeGroupsApi.deleteAttributeGroup(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.attributeGroups }),
  });
}

// ── Product families / attribute templates (epic-catalog-004, user-story-catalog-005) ──
export function useProductFamilies(options?: Opts): UseQueryResult<ProductFamily[], Err> {
  return useQuery<ProductFamily[], Err>({
    queryKey: catalogKeys.productFamilies,
    queryFn: productFamiliesApi.listProductFamilies,
    enabled: options?.enabled ?? true,
  });
}

export function useProductFamily(
  code: string | undefined,
  options?: Opts,
): UseQueryResult<ProductFamily, Err> {
  return useQuery<ProductFamily, Err>({
    queryKey: catalogKeys.productFamily(code ?? ''),
    queryFn: () => productFamiliesApi.getProductFamily(code as string),
    enabled: (options?.enabled ?? true) && Boolean(code),
  });
}

export function useCreateProductFamily() {
  const qc = useQueryClient();
  return useMutation<ProductFamily, Err, ProductFamilyCreateRequest>({
    mutationFn: (body) => productFamiliesApi.createProductFamily(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.productFamilies }),
  });
}

export function useUpdateProductFamily() {
  const qc = useQueryClient();
  return useMutation<ProductFamily, Err, { code: string; body: ProductFamilyUpdateRequest }>({
    mutationFn: ({ code, body }) => productFamiliesApi.updateProductFamily(code, body),
    onSuccess: (data) => {
      qc.setQueryData(catalogKeys.productFamily(data.code), data);
      void qc.invalidateQueries({ queryKey: catalogKeys.productFamilies });
    },
  });
}

export function useDeleteProductFamily() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (code) => productFamiliesApi.deleteProductFamily(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.productFamilies }),
  });
}

export function useAddProductFamilyAttribute() {
  const qc = useQueryClient();
  return useMutation<
    ProductFamilyAttribute,
    Err,
    { code: string; body: ProductFamilyAttributeInput }
  >({
    mutationFn: ({ code, body }) => productFamiliesApi.addProductFamilyAttribute(code, body),
    onSuccess: (_data, { code }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.productFamily(code) });
      void qc.invalidateQueries({ queryKey: catalogKeys.productFamilies });
    },
  });
}

export function useUpdateProductFamilyAttribute() {
  const qc = useQueryClient();
  return useMutation<
    ProductFamilyAttribute,
    Err,
    { code: string; attributeCode: string; body: ProductFamilyAttributeUpdateRequest }
  >({
    mutationFn: ({ code, attributeCode, body }) =>
      productFamiliesApi.updateProductFamilyAttribute(code, attributeCode, body),
    onSuccess: (_data, { code }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.productFamily(code) });
      void qc.invalidateQueries({ queryKey: catalogKeys.productFamilies });
    },
  });
}

export function useDeleteProductFamilyAttribute() {
  const qc = useQueryClient();
  return useMutation<void, Err, { code: string; attributeCode: string }>({
    mutationFn: ({ code, attributeCode }) =>
      productFamiliesApi.deleteProductFamilyAttribute(code, attributeCode),
    onSuccess: (_data, { code }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.productFamily(code) });
      void qc.invalidateQueries({ queryKey: catalogKeys.productFamilies });
    },
  });
}

// ── Measurement families (epic-catalog-005, user-story-catalog-013) ─────────
export function useMeasurementFamilies(options?: Opts): UseQueryResult<MeasurementFamily[], Err> {
  return useQuery<MeasurementFamily[], Err>({
    queryKey: catalogKeys.measurementFamilies,
    queryFn: measurementFamiliesApi.listMeasurementFamilies,
    enabled: options?.enabled ?? true,
  });
}

export function useMeasurementFamily(
  code: string | undefined,
  options?: Opts,
): UseQueryResult<MeasurementFamily, Err> {
  return useQuery<MeasurementFamily, Err>({
    queryKey: catalogKeys.measurementFamily(code ?? ''),
    queryFn: () => measurementFamiliesApi.getMeasurementFamily(code as string),
    enabled: (options?.enabled ?? true) && Boolean(code),
  });
}

export function useCreateMeasurementFamily() {
  const qc = useQueryClient();
  return useMutation<MeasurementFamily, Err, MeasurementFamilyCreateRequest>({
    mutationFn: (body) => measurementFamiliesApi.createMeasurementFamily(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.measurementFamilies }),
  });
}

export function useUpdateMeasurementFamily() {
  const qc = useQueryClient();
  return useMutation<MeasurementFamily, Err, { code: string; body: MeasurementFamilyUpdateRequest }>({
    mutationFn: ({ code, body }) => measurementFamiliesApi.updateMeasurementFamily(code, body),
    onSuccess: (data) => {
      qc.setQueryData(catalogKeys.measurementFamily(data.code), data);
      void qc.invalidateQueries({ queryKey: catalogKeys.measurementFamilies });
    },
  });
}

export function useDeleteMeasurementFamily() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (code) => measurementFamiliesApi.deleteMeasurementFamily(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.measurementFamilies }),
  });
}

// ── Publication workspace: catalogs + lifecycle + preview + runs (epic-catalog-001) ─────────

/**
 * A catalog's computed membership (preview) and run history both depend on its current
 * status/rules/profile — every lifecycle action and every rule mutation refetches both so the
 * Preview/Runs tabs never show stale data after an action taken elsewhere in the workspace.
 */
function invalidatePublicationCatalogDerived(qc: ReturnType<typeof useQueryClient>, catalogId: string) {
  void qc.invalidateQueries({ queryKey: catalogKeys.publicationPreview(catalogId) });
  void qc.invalidateQueries({ queryKey: catalogKeys.publicationRunsRoot(catalogId) });
}

export function usePublicationCatalogs(options?: Opts): UseQueryResult<PublicationCatalog[], Err> {
  return useQuery<PublicationCatalog[], Err>({
    queryKey: catalogKeys.publicationCatalogs,
    queryFn: publicationApi.listPublicationCatalogs,
    enabled: options?.enabled ?? true,
  });
}

export function usePublicationCatalog(
  catalogId: string | undefined,
  options?: Opts,
): UseQueryResult<PublicationCatalog, Err> {
  return useQuery<PublicationCatalog, Err>({
    queryKey: catalogKeys.publicationCatalog(catalogId ?? ''),
    queryFn: () => publicationApi.getPublicationCatalog(catalogId as string),
    enabled: (options?.enabled ?? true) && Boolean(catalogId),
  });
}

export function useCreatePublicationCatalog() {
  const qc = useQueryClient();
  return useMutation<PublicationCatalog, Err, CreatePublicationCatalogRequest>({
    mutationFn: (body) => publicationApi.createPublicationCatalog(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: catalogKeys.publicationCatalogs }),
  });
}

export function useUpdatePublicationCatalog() {
  const qc = useQueryClient();
  return useMutation<
    PublicationCatalog,
    Err,
    { catalogId: string; body: UpdatePublicationCatalogRequest }
  >({
    mutationFn: ({ catalogId, body }) => publicationApi.updatePublicationCatalog(catalogId, body),
    onSuccess: (data) => {
      qc.setQueryData(catalogKeys.publicationCatalog(data.id), data);
      void qc.invalidateQueries({ queryKey: catalogKeys.publicationCatalogs });
    },
  });
}

function useCatalogLifecycleAction(
  action: (catalogId: string) => Promise<PublicationCatalog>,
) {
  const qc = useQueryClient();
  return useMutation<PublicationCatalog, Err, string>({
    mutationFn: (catalogId) => action(catalogId),
    onSuccess: (data) => {
      qc.setQueryData(catalogKeys.publicationCatalog(data.id), data);
      void qc.invalidateQueries({ queryKey: catalogKeys.publicationCatalogs });
      invalidatePublicationCatalogDerived(qc, data.id);
    },
  });
}

export function useActivatePublicationCatalog() {
  return useCatalogLifecycleAction(publicationApi.activatePublicationCatalog);
}

export function usePausePublicationCatalog() {
  return useCatalogLifecycleAction(publicationApi.pausePublicationCatalog);
}

export function useResumePublicationCatalog() {
  return useCatalogLifecycleAction(publicationApi.resumePublicationCatalog);
}

export function useArchivePublicationCatalog() {
  return useCatalogLifecycleAction(publicationApi.archivePublicationCatalog);
}

/** Distinct return type (`PublicationRun`, not `PublicationCatalog`) — kept separate from the lifecycle helper above. */
export function useRepublishPublicationCatalog() {
  const qc = useQueryClient();
  return useMutation<PublicationRun, Err, string>({
    mutationFn: (catalogId) => publicationApi.republishPublicationCatalog(catalogId),
    onSuccess: (_data, catalogId) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.publicationCatalogs });
      void qc.invalidateQueries({ queryKey: catalogKeys.publicationCatalog(catalogId) });
      invalidatePublicationCatalogDerived(qc, catalogId);
    },
  });
}

export function usePublicationCatalogPreview(
  catalogId: string | undefined,
  options?: Opts,
): UseQueryResult<CatalogPreviewResult, Err> {
  return useQuery<CatalogPreviewResult, Err>({
    queryKey: catalogKeys.publicationPreview(catalogId ?? ''),
    queryFn: () => publicationApi.previewPublicationCatalog(catalogId as string),
    enabled: (options?.enabled ?? true) && Boolean(catalogId),
  });
}

export function usePublicationRuns(
  catalogId: string | undefined,
  params: { page?: number; size?: number },
  options?: Opts,
): UseQueryResult<PublicationRunsPage, Err> {
  return useQuery<PublicationRunsPage, Err>({
    queryKey: catalogKeys.publicationRuns(catalogId ?? '', params),
    queryFn: () => publicationApi.listPublicationRuns(catalogId as string, params),
    enabled: (options?.enabled ?? true) && Boolean(catalogId),
    placeholderData: keepPreviousData,
  });
}

export function usePublicationRunDetail(
  catalogId: string | undefined,
  runId: string | undefined,
  options?: Opts,
): UseQueryResult<PublicationRunDetail, Err> {
  return useQuery<PublicationRunDetail, Err>({
    queryKey: catalogKeys.publicationRun(catalogId ?? '', runId ?? ''),
    queryFn: () => publicationApi.getPublicationRunDetail(catalogId as string, runId as string),
    enabled: (options?.enabled ?? true) && Boolean(catalogId) && Boolean(runId),
  });
}

// ── Publication workspace: catalog rules (include/exclude membership) ───────────────────────

export function usePublicationCatalogRules(
  catalogId: string | undefined,
  options?: Opts,
): UseQueryResult<PublicationCatalogRule[], Err> {
  return useQuery<PublicationCatalogRule[], Err>({
    queryKey: catalogKeys.publicationRules(catalogId ?? ''),
    queryFn: () => publicationApi.listPublicationCatalogRules(catalogId as string),
    enabled: (options?.enabled ?? true) && Boolean(catalogId),
  });
}

export function useCreatePublicationCatalogRule() {
  const qc = useQueryClient();
  return useMutation<
    PublicationCatalogRule,
    Err,
    { catalogId: string; body: CreatePublicationCatalogRuleRequest }
  >({
    mutationFn: ({ catalogId, body }) =>
      publicationApi.createPublicationCatalogRule(catalogId, body),
    onSuccess: (_data, { catalogId }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.publicationRules(catalogId) });
      invalidatePublicationCatalogDerived(qc, catalogId);
    },
  });
}

export function useUpdatePublicationCatalogRule() {
  const qc = useQueryClient();
  return useMutation<
    PublicationCatalogRule,
    Err,
    { catalogId: string; ruleId: string; body: UpdatePublicationCatalogRuleRequest }
  >({
    mutationFn: ({ catalogId, ruleId, body }) =>
      publicationApi.updatePublicationCatalogRule(catalogId, ruleId, body),
    onSuccess: (_data, { catalogId }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.publicationRules(catalogId) });
      invalidatePublicationCatalogDerived(qc, catalogId);
    },
  });
}

export function useDeletePublicationCatalogRule() {
  const qc = useQueryClient();
  return useMutation<void, Err, { catalogId: string; ruleId: string }>({
    mutationFn: ({ catalogId, ruleId }) =>
      publicationApi.deletePublicationCatalogRule(catalogId, ruleId),
    onSuccess: (_data, { catalogId }) => {
      void qc.invalidateQueries({ queryKey: catalogKeys.publicationRules(catalogId) });
      invalidatePublicationCatalogDerived(qc, catalogId);
    },
  });
}

// ── Publication workspace: sales channels + catalog bindings ────────────────────────────────

export function usePublicationSalesChannels(
  options?: Opts,
): UseQueryResult<PublicationSalesChannel[], Err> {
  return useQuery<PublicationSalesChannel[], Err>({
    queryKey: catalogKeys.publicationSalesChannels,
    queryFn: publicationApi.listPublicationSalesChannels,
    enabled: options?.enabled ?? true,
  });
}

// ── Channel ↔ catalog bindings ─────────────────────────────────────────────────────────────

export interface PublicationChannelLocalBindings {
  primaryCatalogId: string | null;
  overlays: { catalogId: string; priority: number }[];
}

const EMPTY_LOCAL_BINDINGS: PublicationChannelLocalBindings = { primaryCatalogId: null, overlays: [] };

function toChannelBindings(bindings: PublicationCatalogChannelBinding[]): PublicationChannelLocalBindings {
  const primary = bindings.find((binding) => binding.role === 'PRIMARY') ?? null;
  const overlays = bindings
    .filter((binding) => binding.role === 'OVERLAY')
    .map((binding) => ({ catalogId: binding.catalogId, priority: binding.priority ?? 0 }))
    .sort((a, b) => a.priority - b.priority);
  return { primaryCatalogId: primary?.catalogId ?? null, overlays };
}

export function usePublicationChannelBindings(
  channelId: string,
  options?: Opts,
): UseQueryResult<PublicationChannelLocalBindings, Err> {
  return useQuery<PublicationChannelLocalBindings, Err>({
    queryKey: catalogKeys.publicationChannelBindings(channelId),
    queryFn: async () => toChannelBindings(await publicationApi.listPublicationCatalogChannelBindings(channelId)),
    enabled: (options?.enabled ?? true) && channelId !== '',
  });
}

export function useSetPublicationPrimaryCatalog() {
  const qc = useQueryClient();
  return useMutation<
    PublicationCatalogChannelBinding,
    Err,
    { channelId: string; body: SetPublicationPrimaryCatalogRequest }
  >({
    mutationFn: ({ channelId, body }) => publicationApi.setPublicationPrimaryCatalog(channelId, body),
    onSuccess: (data, { channelId }) => {
      qc.setQueryData<PublicationChannelLocalBindings>(
        catalogKeys.publicationChannelBindings(channelId),
        (prev) => ({ ...(prev ?? EMPTY_LOCAL_BINDINGS), primaryCatalogId: data.catalogId }),
      );
    },
  });
}

export function useBindPublicationOverlayCatalog() {
  const qc = useQueryClient();
  return useMutation<
    PublicationCatalogChannelBinding,
    Err,
    { channelId: string; catalogId: string; body: BindPublicationOverlayCatalogRequest }
  >({
    mutationFn: ({ channelId, catalogId, body }) =>
      publicationApi.bindPublicationOverlayCatalog(channelId, catalogId, body),
    onSuccess: (data, { channelId }) => {
      qc.setQueryData<PublicationChannelLocalBindings>(
        catalogKeys.publicationChannelBindings(channelId),
        (prev) => {
          const base = prev ?? EMPTY_LOCAL_BINDINGS;
          // Re-binding an already-bound overlay updates its priority in place (matches the API's
          // own "re-binding updates priority rather than duplicating" behaviour).
          const withoutThis = base.overlays.filter((o) => o.catalogId !== data.catalogId);
          return {
            ...base,
            overlays: [...withoutThis, { catalogId: data.catalogId, priority: data.priority ?? 0 }].sort(
              (a, b) => a.priority - b.priority,
            ),
          };
        },
      );
    },
  });
}

export function useUnbindPublicationOverlayCatalog() {
  const qc = useQueryClient();
  return useMutation<void, Err, { channelId: string; catalogId: string }>({
    mutationFn: ({ channelId, catalogId }) =>
      publicationApi.unbindPublicationOverlayCatalog(channelId, catalogId),
    onSuccess: (_data, { channelId, catalogId }) => {
      qc.setQueryData<PublicationChannelLocalBindings>(
        catalogKeys.publicationChannelBindings(channelId),
        (prev) => ({
          ...(prev ?? EMPTY_LOCAL_BINDINGS),
          overlays: (prev ?? EMPTY_LOCAL_BINDINGS).overlays.filter((o) => o.catalogId !== catalogId),
        }),
      );
    },
  });
}

// ── Publication workspace: catalog types + publication profile ──────────────────────────────

export function usePublicationCatalogTypes(
  options?: Opts,
): UseQueryResult<PublicationCatalogType[], Err> {
  return useQuery<PublicationCatalogType[], Err>({
    queryKey: catalogKeys.publicationCatalogTypes,
    queryFn: publicationApi.listPublicationCatalogTypes,
    enabled: options?.enabled ?? true,
  });
}

export function useEffectivePublicationProfile(
  code: string | undefined,
  options?: Opts,
): UseQueryResult<EffectivePublicationProfile, Err> {
  return useQuery<EffectivePublicationProfile, Err>({
    queryKey: catalogKeys.publicationProfile(code ?? ''),
    queryFn: () => publicationApi.getEffectivePublicationProfile(code as string),
    enabled: (options?.enabled ?? true) && Boolean(code),
  });
}

export function usePutPublicationProfileOverride() {
  const qc = useQueryClient();
  return useMutation<
    PublicationProfileOverride,
    Err,
    { code: string; body: PublicationProfileOverrideRequest }
  >({
    mutationFn: ({ code, body }) => publicationApi.putPublicationProfileOverride(code, body),
    onSuccess: (_data, { code }) =>
      void qc.invalidateQueries({ queryKey: catalogKeys.publicationProfile(code) }),
  });
}

export function useDeletePublicationProfileOverride() {
  const qc = useQueryClient();
  return useMutation<void, Err, string>({
    mutationFn: (code) => publicationApi.deletePublicationProfileOverride(code),
    onSuccess: (_data, code) =>
      void qc.invalidateQueries({ queryKey: catalogKeys.publicationProfile(code) }),
  });
}
